import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  HOLD_UNTIL_REPLY_SWITCH_MEDIUM_MODAL,
  NODE_DATA_BY_TYPE,
  NODE_TYPES,
} from 'modules/workflows/constants';
import {
  TriggerHoldUntilReplyRuleType,
  TriggerHoldUntilReplyRuleValue,
} from 'modules/workflows/constants/nodes/holdUntilReply';
import {
  addEdges,
  closeModal,
  deleteEdges,
  getDependentEdges,
  getIsNodeEditorReadOnly,
  openModal,
  updateNodeData,
  WorkflowsRootState,
} from 'modules/workflows/ducks';
import { Edge, HoldUntilReplyNodeData, NodeId } from 'modules/workflows/types';
import { useCallback, useState } from 'react';

import { useHoldForReplyEdgeRemapper } from 'modules/workflowNodeEditors/editorComponents/utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { SMSHoldUntilReply, WhatsAppHoldUntilReply } from 'ui/illustrations';
import { Orientation } from 'ui/radioGroup/types';
import { EditorBaseProps } from '../../types';
import { HoldDuration } from './HoldDuration';
import {
  CAMPAIGN_ID,
  DEFAULT_CAMPAIGN_ID,
  DEFAULT_REPLIES,
  HAS_OTHER_REPLY,
  REPLIES,
  TRIGGER_RULE_TYPE,
} from './HoldUntilReply.constants';
import {
  BorderContainer,
  HoldUntilReplyContainer,
  RadioLabelContainer,
  RadioLabelText,
  StyledRadioGroupField,
} from './HoldUntilReply.styles';
import {
  HoldUntilReplyEditorFormSchemaType,
  HoldUntilReplyFormProvider,
} from './HoldUntilReplyFormProvider';
import { Replies } from './Replies';
import { createTriggerRule, parseValuesForTriggerRule } from './TriggerRuleUtils';
import { UnknownReplyCheckBox } from './UnknownReplyCheckBox';
import { WhatsAppCampaignSelector } from './WhatsAppCampaignSelector';
import {
  createHoldDurationForSave,
  getAlternateTriggerRuleType,
  hasAtLeastOneReplyVariant,
} from './utils';

import { colors } from 'ui/theme';
import { HoldUntilReplyCheckQuickReplyGroupsBanner } from './HoldUntilReplyBanners';
import { HoldUntilReplyProvider } from './HoldUntilReplyContext';
import { useQuickReplies } from './hooks/useQuickReplies';

export type HoldUntilReplyEditorProps = EditorBaseProps<HoldUntilReplyNodeData>;

export const HOLD_UNTIL_REPLY_EDITOR_TEST_ID = 'hold-until-reply-editor';

export const HoldUntilReplyCmp = (props: HoldUntilReplyEditorProps) => {
  const { nodeId } = props;
  const edges = useSelector((state) =>
    getDependentEdges(state as WorkflowsRootState, nodeId as NodeId),
  );
  const dispatch = useDispatch();
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  const { control, setValue, handleSubmit } = useFormContext<HoldUntilReplyEditorFormSchemaType>();
  const [replies, campaignId, triggerRuleType] = useWatch({
    control,
    name: [REPLIES, CAMPAIGN_ID, TRIGGER_RULE_TYPE],
  });
  const hasOtherReply = useWatch({ control, name: HAS_OTHER_REPLY });

  const noReplyIndex = hasOtherReply ? replies.length + 1 : replies.length;
  const otherReplyIndex = replies.length;

  const isCloneWarningBannerShown = false;

  const [nodeData] = useState<HoldUntilReplyNodeData>(
    props.nodeData || NODE_DATA_BY_TYPE[NODE_TYPES.HOLD],
  );

  const { filteredQuickReplies } = useQuickReplies();

  const [
    remappedEdges,
    { removeEdgeAt, shiftEdgesOtherAndNoReply, removeOtherReply, addOtherReply, clearEdges },
  ] = useHoldForReplyEdgeRemapper(edges, hasOtherReply);

  const handleUnknownReplyChange = (value: boolean) => {
    // do nothing if in read-only mode
    if (isTileEditorReadOnly) return;

    if (value) {
      addOtherReply(noReplyIndex);
    } else {
      removeOtherReply(otherReplyIndex);
    }
  };

  const onTriggerRuleSelect = useCallback(
    (value: any) => {
      // do nothing if in read-only mode
      if (isTileEditorReadOnly) return;

      // selecting the same trigger rule type should do nothing
      if (value === triggerRuleType) return;

      setValue(TRIGGER_RULE_TYPE, value);

      if (hasAtLeastOneReplyVariant(replies)) {
        dispatch(
          openModal(HOLD_UNTIL_REPLY_SWITCH_MEDIUM_MODAL, {
            triggerRuleType: value,
            handleDeleteAndSwitch: () => {
              setValue(REPLIES, DEFAULT_REPLIES);
              setValue(CAMPAIGN_ID, DEFAULT_CAMPAIGN_ID);
              dispatch(closeModal(HOLD_UNTIL_REPLY_SWITCH_MEDIUM_MODAL));
              clearEdges();
            },
            onClose: () => {
              setValue(TRIGGER_RULE_TYPE, getAlternateTriggerRuleType(value));
              dispatch(closeModal(HOLD_UNTIL_REPLY_SWITCH_MEDIUM_MODAL));
            },
          }),
        );
      } else {
        setValue(REPLIES, DEFAULT_REPLIES);
        clearEdges();
      }
    },
    [dispatch, setValue, replies, isTileEditorReadOnly],
  );

  const handleUpdate = useCallback(
    (values: HoldUntilReplyEditorFormSchemaType) => {
      const { type, hasOtherReply, replies, campaignId } = parseValuesForTriggerRule(values);
      const triggerRuleId = nodeData.triggerRule?.id;
      const triggerRule = createTriggerRule({
        type,
        replies,
        hasOtherReply,
        campaignId,
        triggerRuleId,
      });

      const { expireDays, expireHours, expireMinutes } = values.customDuration;
      const holdDuration = createHoldDurationForSave(values.holdDuration || 0, {
        expireDays,
        expireHours,
        expireMinutes,
      });

      const updatedNodeData = { ...nodeData, triggerRule, ...holdDuration };
      dispatch(updateNodeData(updatedNodeData, nodeId));
      dispatch(deleteEdges(edges));
      dispatch(addEdges(remappedEdges as Edge[]));
    },
    [nodeData, nodeId, dispatch, edges, remappedEdges],
  );

  const banner = <HoldUntilReplyCheckQuickReplyGroupsBanner />;

  return (
    <NodeEditor
      {...props}
      canUpdate={!isTileEditorReadOnly}
      dataTest={HOLD_UNTIL_REPLY_EDITOR_TEST_ID}
      initialNodeData={props.nodeData}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
      onUpdate={() => handleSubmit(handleUpdate)()}
      updatedNodeData={nodeData}
    >
      <HoldUntilReplyContainer>
        <BorderContainer>
          <StyledRadioGroupField
            radioAlignment={'flex-start'}
            orientation={Orientation.HORIZONTAL}
            isBordered
            isOverlapped
            defaultValue={TriggerHoldUntilReplyRuleType.SMS}
            name={TRIGGER_RULE_TYPE}
            onSelect={onTriggerRuleSelect}
            isReadOnly={isTileEditorReadOnly}
            options={[
              {
                id: TriggerHoldUntilReplyRuleValue.SMS,
                label: (
                  <RadioLabelContainer>
                    <SMSHoldUntilReply
                      primaryColor={isTileEditorReadOnly ? colors.grey50 : undefined}
                      secondaryColor={isTileEditorReadOnly ? colors.grey10 : undefined}
                    />
                    <RadioLabelText>{TriggerHoldUntilReplyRuleValue.SMS}</RadioLabelText>
                  </RadioLabelContainer>
                ),
                value: TriggerHoldUntilReplyRuleType.SMS,
              },
              {
                id: TriggerHoldUntilReplyRuleValue.WHATSAPP,
                label: (
                  <RadioLabelContainer>
                    <WhatsAppHoldUntilReply
                      primaryColor={isTileEditorReadOnly ? colors.grey50 : undefined}
                      secondaryColor={isTileEditorReadOnly ? colors.grey10 : undefined}
                      tertiaryColor={isTileEditorReadOnly ? colors.grey25 : undefined}
                    />
                    <RadioLabelText>{TriggerHoldUntilReplyRuleValue.WHATSAPP}</RadioLabelText>
                  </RadioLabelContainer>
                ),
                value: TriggerHoldUntilReplyRuleType.WHATSAPP,
              },
            ]}
          />
          <WhatsAppCampaignSelector isReadOnly={isTileEditorReadOnly} />
        </BorderContainer>
        {triggerRuleType === TriggerHoldUntilReplyRuleType.WHATSAPP &&
        campaignId &&
        filteredQuickReplies.length > 0 &&
        !isTileEditorReadOnly
          ? banner
          : null}
        <Replies
          onAddReply={() => shiftEdgesOtherAndNoReply(otherReplyIndex, noReplyIndex)}
          onRemoveReply={removeEdgeAt}
          isReadOnly={isTileEditorReadOnly}
        />
        <UnknownReplyCheckBox onUnknownReplyChange={handleUnknownReplyChange} isReadOnly />
        <HoldDuration isReadOnly={isTileEditorReadOnly} />
      </HoldUntilReplyContainer>
    </NodeEditor>
  );
};

export const HoldUntilReplyEditor = (props: HoldUntilReplyEditorProps) => (
  <HoldUntilReplyProvider>
    <HoldUntilReplyFormProvider {...props}>
      <HoldUntilReplyCmp {...props} />
    </HoldUntilReplyFormProvider>
  </HoldUntilReplyProvider>
);
